import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Map, Users, Shield, Crown, Store, ArrowRight,
    Heart, Camera, Route, Star, Check,
} from "lucide-react";
import { BRAND } from "../lib/brand";
import { api } from "../lib/api";
import { TICKER_ACTIVITIES as ACTIVITIES } from "../lib/interests";


const HERO = "https://images.unsplash.com/photo-1762374008579-c883ef74267a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxlcGljJTIwaGlraW5nJTIwbW91bnRhaW4lMjBzdW5yaXNlfGVufDB8fHx8MTc3Nzc2OTk0OHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
    { icon: Map, title: "Real GPS routes", text: "Every adventure ships with an interactive route so you can recreate it trail by trail." },
    { icon: Camera, title: "Photo & video journals", text: "Post full trip reports that feel like guidebooks, not just status updates." },
    { icon: Users, title: "Follow & friend", text: "Build your crew. See what your favorite outdoor creators posted this week." },
    { icon: Store, title: "Marketplace", text: "Buy, sell, and trade gear with other adventurers. No middlemen, no nonsense." },
    { icon: Shield, title: "Privacy controls", text: "You choose who sees your location, photos, and full routes." },
    { icon: Crown, title: "Premium perks", text: "Go ad-free, unlock full GPS exports, and earn from your content — coming soon." },
];

/** Compact thousands formatter: 12453 → "12.4k", 999 → "999", 1500000 → "1.5M". */
function compact(n) {
    if (n == null) return "—";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 10_000) return `${Math.round(n / 100) / 10}k`;
    if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

export default function Landing() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let cancel = false;
        api.get("/feed/landing-stats")
            .then(({ data }) => { if (!cancel) setStats(data); })
            .catch(() => { if (!cancel) setStats({}); });
        return () => { cancel = true; };
    }, []);
    return (
        <div className="min-h-screen bg-alpine-mist text-granite">
            {/* Nav */}
            <header className="absolute top-0 inset-x-0 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
                    <Link to="/" data-testid="landing-brand" className="flex items-center gap-3">
                        <img
                            src={BRAND.logoUrl}
                            alt={BRAND.name}
                            className="w-11 h-11 object-contain shrink-0"
                        />
                        <div>
                            <div className="font-display font-black text-xl text-white leading-none tracking-tight">
                                {BRAND.nameWordOne}
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan">
                                {BRAND.nameWordTwo}
                            </div>
                        </div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-white/90">
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/login"
                            data-testid="landing-login-btn"
                            className="inline-flex px-5 py-2 rounded-md text-sm font-bold text-white border border-white/30 hover:bg-white/10 transition-colors"
                        >
                            Log in
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative min-h-[640px] lg:min-h-[780px] w-full overflow-hidden">
                <img src={HERO} alt="Sunrise over mountain ridge" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 hero-vignette" />
                <div className="absolute inset-0 noise-overlay opacity-40" />

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 lg:pt-56 pb-24">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.2em] mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-ember-orange animate-pulse" /> Built by people who actually go outside
                        </div>
                        <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
                            Real adventures.<br />
                            Real people.<br />
                            <span className="text-ember-orange">Real outdoors.</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-white/80 max-w-xl mb-8 leading-relaxed">
                            RealWorld Outdoors is where hikers, climbers, overlanders, paddlers and anglers post reusable adventure guides — photos, GPS routes, the whole trip report. Find yours. Go do it.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link
                                to="/signup"
                                data-testid="hero-join-btn"
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 transition-all"
                            >
                                Join free <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl" data-testid="landing-stats">
                            {[
                                { label: "Active trails", value: compact(stats?.adventures_count), testid: "stat-active-trails" },
                                { label: "Adventurers",   value: compact(stats?.users_count),      testid: "stat-adventurers" },
                                { label: "Activities",    value: compact(stats?.activities_count), testid: "stat-activities" },
                            ].map((s) => (
                                <div key={s.label} data-testid={s.testid}>
                                    <div className="font-display font-black text-white text-3xl sm:text-4xl">{s.value}</div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-trail-tan mt-1">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Activities strip */}
            <section className="bg-midnight-pine py-6 border-y border-deep-evergreen overflow-hidden">
                <div className="flex gap-12 whitespace-nowrap animate-[scroll_45s_linear_infinite]">
                    {[...ACTIVITIES, ...ACTIVITIES].map((a, i) => (
                        <span key={i} className="font-display font-bold text-3xl sm:text-4xl text-trail-tan/80 uppercase tracking-tighter">
                            {a} <span className="text-ember-orange mx-6">/</span>
                        </span>
                    ))}
                </div>
                <style>{`@keyframes scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
            </section>

            {/* Features */}
            <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <div className="max-w-3xl">
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">
                        What you get
                    </div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl text-midnight-pine tracking-tight leading-[1.05] mb-4">
                        Reusable adventure guides, not another feed.
                    </h2>
                    <p className="text-lg text-slate-gray">
                        Every post is a guidebook page. Photos, descriptions, difficulty, GPS routes — everything you need to go recreate it.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.title}
                            className="bg-snowcap border border-ridge-line rounded-lg p-7 hover:border-ember-orange hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="w-11 h-11 rounded-md bg-trail-tan flex items-center justify-center mb-4">
                                <f.icon className="w-5 h-5 text-midnight-pine" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-granite tracking-tight mb-2">{f.title}</h3>
                            <p className="text-sm text-slate-gray leading-relaxed">{f.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing preview */}
            <section className="bg-snowcap border-y border-ridge-line py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-4">
                        <div className="max-w-2xl">
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-3">Pricing</div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl text-midnight-pine tracking-tight leading-[1.05]">
                                Free to explore. Pay only if you want more.
                            </h2>
                        </div>
                        <Link
                            to="/pricing"
                            data-testid="landing-pricing-cta"
                            className="inline-flex items-center gap-2 text-sm font-bold text-ember-orange hover:gap-3 transition-all"
                        >
                            See full comparison <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { name: "Free", price: "$0", tag: "Explore the community", features: ["Browse adventures", "View GPS routes", "Basic profile", "Ads supported"] },
                            { name: "Adventure", price: "$5", tag: "Post & engage", features: ["Post unlimited adventures", "Comment & message", "Enhanced profile", "Ads supported"], popular: true },
                            { name: "Premium", price: "$10", tag: "The full toolkit", features: ["Full GPS access", "Marketplace", "No ads", "Premium badge"], dark: true },
                        ].map((p) => (
                            <div
                                key={p.name}
                                className={`rounded-lg p-7 border transition-all duration-300 hover:-translate-y-1 relative ${
                                    p.popular
                                        ? "bg-snowcap border-ember-orange shadow-lg"
                                        : p.dark
                                        ? "bg-midnight-pine text-white border-deep-evergreen"
                                        : "bg-alpine-mist border-ridge-line"
                                }`}
                            >
                                {p.popular && (
                                    <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest">
                                        Most popular
                                    </div>
                                )}
                                <div className="text-xs font-bold uppercase tracking-widest mb-2 opacity-70">{p.tag}</div>
                                <div className="font-display font-black text-3xl mb-1">{p.name}</div>
                                <div className="font-display font-black text-4xl mb-5">
                                    {p.price}
                                    <span className="text-base font-normal opacity-60">/mo</span>
                                </div>
                                <ul className="space-y-2 mb-6">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-sm">
                                            <Check className={`w-4 h-4 shrink-0 mt-0.5 ${p.dark ? "text-ember-orange" : "text-moss"}`} />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/pricing"
                                    data-testid={`landing-pricing-${p.name.toLowerCase()}-btn`}
                                    className={`block w-full text-center py-2.5 rounded-md font-bold text-sm transition-all active:scale-95 ${
                                        p.popular
                                            ? "bg-ember-orange text-white hover:bg-ember-dark"
                                            : p.dark
                                            ? "bg-ember-orange text-white hover:bg-ember-dark"
                                            : "bg-midnight-pine text-white hover:bg-deep-evergreen"
                                    }`}
                                >
                                    Choose {p.name}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonial / CTA */}
            <section className="relative py-24 bg-midnight-pine text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20 noise-overlay" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="flex justify-center gap-1 mb-6">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Star key={i} className="w-5 h-5 fill-ember-orange text-ember-orange" />
                        ))}
                    </div>
                    <blockquote className="font-display font-bold text-2xl sm:text-4xl leading-tight tracking-tight mb-6">
                        "Found a three-day loop through the Wind Rivers on RealWorld Outdoors last spring. Downloaded the GPS, packed my kit, did it in July. Exactly what the post promised."
                    </blockquote>
                    <div className="text-sm font-bold uppercase tracking-[0.2em] text-trail-tan">
                        Brit M. — Jackson, WY
                    </div>

                    <div className="mt-12">
                        <Link
                            to="/signup"
                            data-testid="cta-signup-btn"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 transition-all"
                        >
                            Start your first trail report <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-deep-evergreen text-white/70 py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-2 mb-3">
                            <img
                                src={BRAND.logoUrl}
                                alt={BRAND.name}
                                className="w-10 h-10 object-contain"
                            />
                            <span className="font-display font-black text-xl text-white">{BRAND.name}</span>
                        </div>
                        <p className="text-sm max-w-sm leading-relaxed">
                            {BRAND.footerLine}
                        </p>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan mb-3">Legal</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-ember-orange">Terms</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-ember-orange">Privacy</Link></li>
                            <li><Link to="/community-guidelines" data-testid="footer-community-guidelines" className="hover:text-ember-orange">Community guidelines</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 mt-10 border-t border-white/10 text-xs text-white/50">
                    © {new Date().getFullYear()} {BRAND.name}. Go outside.
                </div>
            </footer>
        </div>
    );
}
